import axiosInstance from '@/app/api/axiosInstance'
import { QueryParams } from '@/app/types/common'

export interface ConfigKV {
  id: number
  key: string
  value: string
  description: string | null
  scope: 'global' | 'organization' | 'site'
  organization_id: number | null
  site_id: number | null
  data_type: 'string' | 'number' | 'boolean' | 'json'
  created_at: string
  updated_at: string
  created_by: number | null
  updated_by: number | null
  is_encrypted: boolean
  encrypted_value: string | null
  expires_at: string | null
  is_deleted: boolean
  metadata: Record<string, any> | null
  cache_duration_seconds: number | null
  version: number
}

export interface ConfigKVCreate {
  key: string
  value: string
  description?: string
  scope: 'global' | 'organization' | 'site'
  organization_id?: number
  site_id?: number
  data_type?: 'string' | 'number' | 'boolean' | 'json'
  is_encrypted?: boolean
  expires_at?: string
  metadata?: Record<string, any>
  cache_duration_seconds?: number
}

export interface ConfigKVUpdate {
  value?: string
  description?: string
  data_type?: 'string' | 'number' | 'boolean' | 'json'
  is_encrypted?: boolean
  expires_at?: string
  metadata?: Record<string, any>
  cache_duration_seconds?: number
}

export interface ConfigKVBulkCreate {
  configs: ConfigKVCreate[]
}

export interface ConfigKVBulkDelete {
  keys: string[]
  scope: 'global' | 'organization' | 'site'
  organization_id?: number
  site_id?: number
}

export interface ConfigKVResponse {
  total: number
  items: ConfigKV[]
  page: number
  page_size: number
}

export const listConfigKVs = async (
  params: QueryParams & {
    scope?: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
    search?: string
  }
): Promise<ConfigKVResponse> => {
  const response = await axiosInstance.get('/config-kv/', { params })
  return response.data
}

export const getConfigKV = async (
  key: string,
  params: {
    scope: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
  }
): Promise<ConfigKV> => {
  const response = await axiosInstance.get(`/config-kv/${key}`, { params })
  return response.data
}

export const createConfigKV = async (data: ConfigKVCreate): Promise<ConfigKV> => {
  const response = await axiosInstance.post('/config-kv/', data)
  return response.data
}

export const updateConfigKV = async (
  id: number,
  params: {
    scope: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
  },
  data: ConfigKVUpdate
): Promise<ConfigKV> => {
  const response = await axiosInstance.put(`/config-kv/${id}`, data, { params })
  return response.data
}

export const deleteConfigKV = async (
  key: string,
  params: {
    scope: 'global' | 'organization' | 'site'
    organization_id?: number
    site_id?: number
  }
): Promise<void> => {
  await axiosInstance.delete(`/config-kv/${key}`, { params })
}

export const bulkCreateConfigKVs = async (data: ConfigKVBulkCreate): Promise<ConfigKV[]> => {
  const response = await axiosInstance.post('/config-kv/bulk', data)
  return response.data
}

export const bulkDeleteConfigKVs = async (data: ConfigKVBulkDelete): Promise<void> => {
  await axiosInstance.post('/config-kv/bulk-delete', data)
}

export const exportConfigKVs = async (params: {
  scope?: 'global' | 'organization' | 'site'
  organization_id?: number
  site_id?: number
  format?: 'json' | 'csv'
}): Promise<Blob> => {
  const response = await axiosInstance.get('/config-kv/export', {
    params,
    responseType: 'blob'
  })
  return response.data
}

export const importConfigKVs = async (file: File, params: {
  scope: 'global' | 'organization' | 'site'
  organization_id?: number
  site_id?: number
}): Promise<{ imported: number; errors: string[] }> => {
  const formData = new FormData()
  formData.append('file', file)
  
  const response = await axiosInstance.post('/config-kv/import', formData, {
    params,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return response.data
}